import React, { useMemo } from 'react';
import type { IPropsWithTestId } from '../../../../constants/data-element-tracking-ids';
import { DataElementId } from '../../../../constants/data-element-tracking-ids';
import { ModuleWithBanner } from '../module-with-banner';
import type { IModuleWithBannerBasedOnEnrollmentStatusData } from '../../../../dotcms/dotcms-models/dotcms-module-with-banner-based-on-enrollment-status';
import { useReadyToEnrollModule } from './hooks';
import { getDataBasedOnEnrollmentStatus } from '../../../../dotcms/dotcms-models/model-based-on-enrollment-status-helper';
import { useGetRouteToIssuerPage } from '../../../../app/get-route-to-page';
import HtmlBox from '../../../../component_library/components/HtmlBox';
import { OperationButton } from '../../../OperationButton';
import { useOperationButton } from '../../../OperationButton/hook';
import type { ButtonVariant } from '../../../../component_library/components/Button';
import { OperationButtonAction } from '../../../OperationButton/hook/use-operation-button';

export interface IProps {
  data: IModuleWithBannerBasedOnEnrollmentStatusData;
}

const ReadyToEnrollInnerComponent: React.FC<IProps> = ({ data }) => {
  const { buttonPages, userStates, isEnrollmentOrScheduledEnrollmentOpen } =
    useReadyToEnrollModule();
  const { action } = useOperationButton();

  const buttonPage =
    getDataBasedOnEnrollmentStatus(
      buttonPages,
      userStates,
      isEnrollmentOrScheduledEnrollmentOpen,
    ) ?? buttonPages.defaultData;
  const buttonUrl = useGetRouteToIssuerPage(buttonPage);

  const pageData = getDataBasedOnEnrollmentStatus(
    data,
    userStates,
    isEnrollmentOrScheduledEnrollmentOpen,
  );

  const renderButtonElement = useMemo(
    () =>
      (action
        ? (
          props: IPropsWithTestId & {
            label: string | undefined;
            buttonVariant: ButtonVariant;
          },
        ) => (
          <OperationButton
            dataElementId={props.dataElementId}
            variant={props.buttonVariant}
            label={
                action === OperationButtonAction.SCHEDULE_ENROLLMENT
                  ? undefined
                  : props.label
              }
          />
        )
        : undefined),
    [action],
  );

  if (!pageData) {
    return null;
  }

  const { header, button, contentHtml } = pageData;

  return (
    <ModuleWithBanner
      headlineHtml={header}
      renderButtonElement={renderButtonElement}
      button={
        button
          ? {
            buttonCtaText: button.text,
            buttonDestinationLink: buttonUrl ?? button.url,
            dataElementId:
                DataElementId.READY_TO_ENROLL_BANNER_LINK_ENROLL_CTA,
          }
          : undefined
      }
    >
      {contentHtml && <HtmlBox html={contentHtml} />}
    </ModuleWithBanner>
  );
};

export default ReadyToEnrollInnerComponent;
